import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  TextInput,
  TextArea,
  NumberInput,
  Toggle,
  Modal,
  Tile,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  InlineNotification,
  Loading,
  InlineLoading,
} from '@carbon/react';
import {
  TrashCan,
  CheckmarkFilled,
  Warning,
  Copy,
  Renew,
  StopFilledAlt,
} from '@carbon/react/icons';
import { useNavigate } from 'react-router-dom';

const API_BASE = '/mlapi/premium/integration/a2a';
const GRANTS = `${API_BASE}/grants`;
const REMOTES = `${API_BASE}/remotes`;
const AUDIT = `${API_BASE}/audit`;

const A2AIntegrationPage = ({ token }) => {
  const nav = useNavigate();

  const hdr = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
  const authHdr = { Authorization: `Bearer ${token}` };

  // ── state: inbound grants ─────────────────────────────────────────
  const [grants, setGrants] = useState([]);
  const [grantsLoading, setGrantsLoading] = useState(true);
  const [grantModalOpen, setGrantModalOpen] = useState(false);
  const [newGrantLabel, setNewGrantLabel] = useState('');
  const [newGrantCap, setNewGrantCap] = useState(5);
  const [newGrantRate, setNewGrantRate] = useState(60);
  const [newGrantSendEmail, setNewGrantSendEmail] = useState(true);
  const [newGrantExecCode, setNewGrantExecCode] = useState(true);
  const [newGrantNotify, setNewGrantNotify] = useState(false);
  const [newGrantPrompt, setNewGrantPrompt] = useState('');
  const [newGrantErr, setNewGrantErr] = useState('');
  const [savingGrant, setSavingGrant] = useState(false);
  const [issuedToken, setIssuedToken] = useState(null);
  const [rotatedToken, setRotatedToken] = useState(null);

  // ── state: outbound remotes ───────────────────────────────────────
  const [remotes, setRemotes] = useState([]);
  const [remotesLoading, setRemotesLoading] = useState(true);
  const [remoteModalOpen, setRemoteModalOpen] = useState(false);
  const [newRemoteName, setNewRemoteName] = useState('');
  const [newRemoteDesc, setNewRemoteDesc] = useState('');
  const [newRemoteCardUrl, setNewRemoteCardUrl] = useState('');
  const [newRemoteToken, setNewRemoteToken] = useState('');
  const [newRemoteErr, setNewRemoteErr] = useState('');
  const [savingRemote, setSavingRemote] = useState(false);
  const [testingRemote, setTestingRemote] = useState(null);
  const [banner, setBanner] = useState(null);

  // ── state: audit ──────────────────────────────────────────────────
  const [audit, setAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(true);

  const loadGrants = useCallback(() => {
    setGrantsLoading(true);
    fetch(GRANTS, { headers: authHdr })
      .then(r => r.json())
      .then(d => setGrants(Array.isArray(d) ? d : []))
      .catch(console.error)
      .finally(() => setGrantsLoading(false));
  }, [token]); // eslint-disable-line

  const loadRemotes = useCallback(() => {
    setRemotesLoading(true);
    fetch(REMOTES, { headers: authHdr })
      .then(r => r.json())
      .then(d => setRemotes(Array.isArray(d) ? d : []))
      .catch(console.error)
      .finally(() => setRemotesLoading(false));
  }, [token]); // eslint-disable-line

  const loadAudit = useCallback(() => {
    setAuditLoading(true);
    fetch(AUDIT, { headers: authHdr })
      .then(r => r.json())
      .then(d => setAudit(Array.isArray(d) ? d : []))
      .catch(console.error)
      .finally(() => setAuditLoading(false));
  }, [token]); // eslint-disable-line

  useEffect(() => {
    loadGrants();
    loadRemotes();
    loadAudit();
  }, [loadGrants, loadRemotes, loadAudit]);

  // ── grant actions ─────────────────────────────────────────────────
  const openNewGrant = () => {
    setNewGrantLabel('');
    setNewGrantCap(5);
    setNewGrantRate(60);
    setNewGrantSendEmail(true);
    setNewGrantExecCode(true);
    setNewGrantNotify(false);
    setNewGrantPrompt('');
    setNewGrantErr('');
    setGrantModalOpen(true);
  };

  const createGrant = () => {
    if (!newGrantLabel.trim()) {
      setNewGrantErr('Label is required');
      return;
    }
    setSavingGrant(true);
    setNewGrantErr('');
    fetch(GRANTS, {
      method: 'POST',
      headers: hdr,
      body: JSON.stringify({
        label: newGrantLabel.trim(),
        monthly_usd_cap: Number(newGrantCap),
        rate_limit_per_hour: Number(newGrantRate),
        send_email_enabled: newGrantSendEmail,
        execute_code_enabled: newGrantExecCode,
        notify_on_call: newGrantNotify,
        custom_system_prompt: newGrantPrompt || null,
      }),
    })
      .then(r => r.json().then(d => ({ ok: r.ok, data: d })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data.detail || 'Failed to create grant');
        setGrants(prev => [data, ...prev]);
        setIssuedToken({ token: data.token, label: data.label });
        setGrantModalOpen(false);
      })
      .catch(e => setNewGrantErr(e.message))
      .finally(() => setSavingGrant(false));
  };

  const revokeGrant = id => {
    if (
      !window.confirm(
        'Revoke this grant? The bearer token will stop working immediately.'
      )
    )
      return;
    fetch(`${GRANTS}/${id}/revoke`, { method: 'POST', headers: hdr })
      .then(r => r.ok && loadGrants())
      .catch(() => alert('Revoke failed'));
  };

  const rotateGrant = id => {
    if (
      !window.confirm(
        'Rotate token? The current token will stop working immediately. You will be shown the new token once.'
      )
    )
      return;
    fetch(`${GRANTS}/${id}/rotate`, { method: 'POST', headers: hdr })
      .then(r => r.json().then(d => ({ ok: r.ok, data: d })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data.detail || 'Rotate failed');
        const grant = grants.find(g => g.id === id);
        setRotatedToken({
          token: data.token,
          label: grant ? grant.label : 'grant',
        });
      })
      .catch(e => alert(e.message));
  };

  const deleteGrant = id => {
    if (
      !window.confirm(
        'Permanently delete this grant and all audit references to it? This cannot be undone.'
      )
    )
      return;
    fetch(`${GRANTS}/${id}`, { method: 'DELETE', headers: hdr }).then(r => {
      if (r.ok) setGrants(prev => prev.filter(g => g.id !== id));
      else alert('Delete failed');
    });
  };

  // ── remote actions ────────────────────────────────────────────────
  const openNewRemote = () => {
    setNewRemoteName('');
    setNewRemoteDesc('');
    setNewRemoteCardUrl('');
    setNewRemoteToken('');
    setNewRemoteErr('');
    setRemoteModalOpen(true);
  };

  const createRemote = () => {
    if (!newRemoteName.trim()) {
      setNewRemoteErr('Name is required');
      return;
    }
    if (!newRemoteCardUrl.trim()) {
      setNewRemoteErr('Agent card URL is required');
      return;
    }
    if (!newRemoteToken.trim()) {
      setNewRemoteErr('Bearer token is required');
      return;
    }
    setSavingRemote(true);
    setNewRemoteErr('');
    fetch(REMOTES, {
      method: 'POST',
      headers: hdr,
      body: JSON.stringify({
        name: newRemoteName.trim(),
        description: newRemoteDesc.trim(),
        agent_card_url: newRemoteCardUrl.trim(),
        bearer_token: newRemoteToken.trim(),
      }),
    })
      .then(r => r.json().then(d => ({ ok: r.ok, data: d })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data.detail || 'Failed to register remote');
        setRemotes(prev => [data, ...prev]);
        setBanner({ name: data.name });
        setRemoteModalOpen(false);
      })
      .catch(e => setNewRemoteErr(e.message))
      .finally(() => setSavingRemote(false));
  };

  const deleteRemote = id => {
    if (!window.confirm('Remove this remote agent?')) return;
    fetch(`${REMOTES}/${id}`, { method: 'DELETE', headers: hdr }).then(
      r => r.ok && setRemotes(prev => prev.filter(r => r.id !== id))
    );
  };

  const testRemote = remote => {
    setTestingRemote(remote.id);
    fetch(`${REMOTES}/${remote.id}/test`, { method: 'POST', headers: hdr })
      .then(r => r.json().then(d => ({ ok: r.ok, data: d })))
      .then(({ ok, data }) => {
        if (!ok) {
          alert(`✗ ${remote.name}: ${data.detail}`);
          return;
        }
        const lines = [
          `${data.ping_ok ? '✓' : '⚠'} ${remote.name}`,
          `Card: "${data.agent_name}" v${data.agent_version}`,
          `Skills: ${(data.skills || []).join(', ') || '(none listed)'}`,
          data.ping_ok
            ? `✓ Authenticated ping succeeded — the remote replied: "${data.ping_result}"`
            : `⚠ Authenticated ping: ${data.ping_result}`,
        ];
        alert(lines.join('\n'));
        loadAudit();
      })
      .catch(() => alert('Test failed'))
      .finally(() => setTestingRemote(null));
  };

  const copyToClipboard = text => {
    navigator.clipboard?.writeText(text).then(
      () => alert('Copied to clipboard'),
      () => alert('Copy failed; please select the text manually')
    );
  };

  // ── render helpers ────────────────────────────────────────────────
  const renderGrants = () => (
    <>
      <p>
        Issue a <strong>grant</strong> to let another party's agent call into
        your Fresh Focus AI account via the A2A protocol. Grants are bearer
        tokens — share them only with people you trust. You can revoke a grant
        at any time and it stops working immediately.
      </p>
      <Button
        kind="primary"
        onClick={openNewGrant}
        style={{ marginTop: '1rem' }}>
        Issue new grant
      </Button>
      <div style={{ marginTop: '1.5rem' }}>
        {grantsLoading ? (
          <Loading active withOverlay={false} />
        ) : grants.length ? (
          grants.map(g => {
            const spend = g.spend_this_month_usd || 0;
            const cap = g.monthly_usd_cap || 0;
            const pctSpent = cap > 0 ? Math.round((spend / cap) * 100) : 0;
            return (
              <Tile key={g.id} style={{ marginBottom: '1rem' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}>
                  <div>
                    <h3 style={{ marginBottom: '0.25rem' }}>
                      {g.label}{' '}
                      {g.revoked && (
                        <span style={{ color: '#da1e28', fontSize: '0.9rem' }}>
                          (revoked)
                        </span>
                      )}
                    </h3>
                    <p
                      className="bx--type-caption"
                      style={{ marginBottom: '0.1rem', color: '#6f6f6f' }}>
                      Created {new Date(g.created_at).toLocaleDateString()}{' '}
                      &mdash; {g.total_calls || 0} call
                      {g.total_calls === 1 ? '' : 's'}
                      {g.last_used_at
                        ? ` (last: ${new Date(
                            g.last_used_at
                          ).toLocaleString()})`
                        : ''}
                    </p>
                    <p
                      className="bx--type-caption"
                      style={{ marginBottom: '0.1rem' }}>
                      Spend this month: <strong>${spend.toFixed(4)}</strong> of
                      ${cap.toFixed(2)} ({pctSpent}%) &mdash; Rate limit:{' '}
                      {g.rate_limit_per_hour}/hour
                    </p>
                    <p
                      className="bx--type-caption"
                      style={{ color: '#6f6f6f' }}>
                      Email: {g.send_email_enabled ? 'allowed' : 'blocked'}{' '}
                      &middot; Code execution:{' '}
                      {g.execute_code_enabled ? 'allowed' : 'blocked'}
                      {g.notify_on_call && ' · notifies on call'}
                    </p>
                  </div>
                </div>
                <div
                  style={{
                    marginTop: '1rem',
                    display: 'flex',
                    gap: '0.5rem',
                    flexWrap: 'wrap',
                  }}>
                  {!g.revoked && (
                    <Button
                      kind="tertiary"
                      size="sm"
                      renderIcon={Renew}
                      onClick={() => rotateGrant(g.id)}>
                      Rotate token
                    </Button>
                  )}
                  {!g.revoked && (
                    <Button
                      kind="danger--tertiary"
                      size="sm"
                      renderIcon={StopFilledAlt}
                      onClick={() => revokeGrant(g.id)}>
                      Revoke
                    </Button>
                  )}
                  <Button
                    kind="danger--tertiary"
                    size="sm"
                    renderIcon={TrashCan}
                    onClick={() => deleteGrant(g.id)}>
                    Delete
                  </Button>
                </div>
              </Tile>
            );
          })
        ) : (
          <p style={{ color: '#6f6f6f' }}>No grants issued yet.</p>
        )}
      </div>
    </>
  );

  const renderRemotes = () => (
    <>
      <p>
        Register a <strong>remote agent</strong> so your Fresh Focus AI agent
        can call out to it via the A2A protocol. You'll need the agent's public{' '}
        <code>agent.json</code> URL and a bearer token that agent's owner issued
        to you.
      </p>
      <Button
        kind="primary"
        onClick={openNewRemote}
        style={{ marginTop: '1rem' }}>
        Register remote agent
      </Button>
      {banner && (
        <InlineNotification
          lowContrast
          kind="success"
          title={
            <span>
              <CheckmarkFilled style={{ marginRight: '0.5rem' }} />
              {`"${banner.name}" registered`}
            </span>
          }
          subtitle="Your FFAI agent can now call this remote via the call_a2a_agent tool."
          onClose={() => setBanner(null)}
          hideCloseButton={false}
          style={{ marginTop: '1rem' }}
        />
      )}
      <div style={{ marginTop: '1.5rem' }}>
        {remotesLoading ? (
          <Loading active withOverlay={false} />
        ) : remotes.length ? (
          remotes.map(r => (
            <Tile key={r.id} style={{ marginBottom: '1rem' }}>
              <h3 style={{ marginBottom: '0.25rem' }}>{r.name}</h3>
              {r.description && (
                <p style={{ marginBottom: '0.25rem' }}>{r.description}</p>
              )}
              <p
                className="bx--type-caption"
                style={{ marginBottom: '0.1rem' }}>
                Card: {r.agent_card_url}
              </p>
              <p className="bx--type-caption" style={{ color: '#6f6f6f' }}>
                Endpoint: {r.endpoint_url || '(none)'} &mdash; Added{' '}
                {new Date(r.created_at).toLocaleDateString()}
              </p>
              <div
                style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                {testingRemote === r.id ? (
                  <InlineLoading description="Testing…" />
                ) : (
                  <Button
                    kind="tertiary"
                    size="sm"
                    onClick={() => testRemote(r)}>
                    Test connection
                  </Button>
                )}
                <Button
                  kind="danger--tertiary"
                  size="sm"
                  renderIcon={TrashCan}
                  onClick={() => deleteRemote(r.id)}>
                  Remove
                </Button>
              </div>
            </Tile>
          ))
        ) : (
          <p style={{ color: '#6f6f6f' }}>No remote agents registered yet.</p>
        )}
      </div>
    </>
  );

  const renderAudit = () => (
    <>
      <p>Most recent inbound A2A calls made against your grants (last 500).</p>
      <Button
        kind="tertiary"
        size="sm"
        onClick={loadAudit}
        style={{ marginTop: '0.5rem' }}>
        Refresh
      </Button>
      <div style={{ marginTop: '1rem' }}>
        {auditLoading ? (
          <Loading active withOverlay={false} />
        ) : audit.length ? (
          audit.map((a, i) => (
            <Tile key={i} style={{ marginBottom: '0.5rem' }}>
              <p style={{ marginBottom: '0.25rem' }}>
                <strong>{a.grant_label || a.grant_id}</strong>{' '}
                <span
                  style={{
                    color: a.state === 'completed' ? '#198038' : '#da1e28',
                  }}>
                  ({a.state})
                </span>
              </p>
              <p
                className="bx--type-caption"
                style={{ marginBottom: '0.1rem', color: '#6f6f6f' }}>
                {new Date(a.ts).toLocaleString()} &mdash;{' '}
                {a.cost_usd != null
                  ? `$${Number(a.cost_usd).toFixed(6)} · `
                  : ''}
                {a.input_tokens || 0} in / {a.output_tokens || 0} out
                {a.tools_used && a.tools_used.length
                  ? ` · tools: ${a.tools_used.join(', ')}`
                  : ''}
              </p>
              {a.input_preview && (
                <p
                  className="bx--type-caption"
                  style={{ marginBottom: '0.1rem' }}>
                  <strong>In:</strong> {a.input_preview}
                </p>
              )}
              {a.response_preview && (
                <p className="bx--type-caption">
                  <strong>Out:</strong> {a.response_preview}
                </p>
              )}
              {a.error && (
                <p className="bx--type-caption" style={{ color: '#da1e28' }}>
                  <strong>Error:</strong> {a.error}
                </p>
              )}
            </Tile>
          ))
        ) : (
          <p style={{ color: '#6f6f6f' }}>No A2A calls yet.</p>
        )}
      </div>
    </>
  );

  return (
    <div className="a2a-integration-page">
      <div className="a2a-integration-container">
        <h1>Agent-to-Agent (A2A) Integration</h1>
        <p>
          A2A lets Fresh Focus AI talk to other AI agents (FFAI accounts or
          third-party platforms) using the open{' '}
          <a
            href="https://a2a-protocol.org/"
            target="_blank"
            rel="noopener noreferrer">
            Agent2Agent protocol
          </a>
          . Issue <strong>grants</strong> so others can call your agent.
          Register <strong>remote agents</strong> so your agent can call them.
          See the{' '}
          <a href="/docs/a2a.html" target="_blank" rel="noopener noreferrer">
            A2A docs
          </a>{' '}
          for setup details and the security model.
        </p>

        <InlineNotification
          lowContrast
          kind="warning"
          title={
            <span>
              <Warning style={{ marginRight: '0.5rem' }} />
              Treat grant tokens like passwords
            </span>
          }
          subtitle="Anyone with a grant token can invoke your FFAI agent with the tools you permit — including sending email on your behalf and executing code. Only share grants with parties you trust."
          hideCloseButton
          style={{ marginTop: '1rem', marginBottom: '1rem' }}
        />

        <Tabs>
          <TabList aria-label="A2A sections">
            <Tab>Inbound grants ({grants.length})</Tab>
            <Tab>Outbound agents ({remotes.length})</Tab>
            <Tab>Activity ({audit.length})</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>{renderGrants()}</TabPanel>
            <TabPanel>{renderRemotes()}</TabPanel>
            <TabPanel>{renderAudit()}</TabPanel>
          </TabPanels>
        </Tabs>

        {/* ── Issue-grant modal ──────────────────────────────────── */}
        <Modal
          open={grantModalOpen}
          modalHeading="Issue a new A2A grant"
          primaryButtonText={savingGrant ? 'Creating…' : 'Create grant'}
          primaryButtonDisabled={savingGrant}
          secondaryButtonText="Cancel"
          onRequestClose={() => setGrantModalOpen(false)}
          onRequestSubmit={createGrant}
          size="md">
          <TextInput
            id="a2a-grant-label"
            labelText="Label"
            placeholder="e.g. Dylan's FFAI agent, Acme corp integration"
            helperText="Human-readable name so you can find it in audit logs later."
            value={newGrantLabel}
            onChange={e => setNewGrantLabel(e.target.value)}
            className="a2a-form-field"
          />
          <NumberInput
            id="a2a-grant-cap"
            label="Monthly spend cap (USD)"
            helperText="Max LLM spend per month for this grant. Cannot exceed your $15/mo account cap."
            min={0}
            max={15}
            step={0.5}
            value={newGrantCap}
            onChange={(_, { value }) => setNewGrantCap(value)}
            className="a2a-form-field"
          />
          <NumberInput
            id="a2a-grant-rate"
            label="Rate limit (calls per hour)"
            helperText="Caps inbound calls to protect from runaway loops."
            min={1}
            max={500}
            step={10}
            value={newGrantRate}
            onChange={(_, { value }) => setNewGrantRate(value)}
            className="a2a-form-field"
          />
          <div className="a2a-form-field">
            <Toggle
              id="a2a-grant-email"
              labelText="Allow send_email tool"
              labelA="Blocked"
              labelB="Allowed"
              toggled={newGrantSendEmail}
              onToggle={setNewGrantSendEmail}
            />
          </div>
          <div className="a2a-form-field">
            <Toggle
              id="a2a-grant-code"
              labelText="Allow execute_code tool (sandboxed)"
              labelA="Blocked"
              labelB="Allowed"
              toggled={newGrantExecCode}
              onToggle={setNewGrantExecCode}
            />
          </div>
          <div className="a2a-form-field">
            <Toggle
              id="a2a-grant-notify"
              labelText="Email me on every inbound call"
              labelA="Off"
              labelB="On"
              toggled={newGrantNotify}
              onToggle={setNewGrantNotify}
            />
          </div>
          <TextArea
            id="a2a-grant-prompt"
            labelText="Custom system prompt (optional)"
            helperText="Additional instructions appended to the security overlay when serving calls from this grant."
            rows={3}
            value={newGrantPrompt}
            onChange={e => setNewGrantPrompt(e.target.value)}
          />
          {newGrantErr && (
            <p
              style={{
                color: '#da1e28',
                marginTop: '1rem',
                fontSize: '0.875rem',
              }}>
              {newGrantErr}
            </p>
          )}
        </Modal>

        {/* ── Issued-token modal (one-time reveal) ─────────────── */}
        <Modal
          open={!!issuedToken}
          modalHeading={`Grant issued: "${issuedToken?.label}"`}
          primaryButtonText="I've saved the token"
          secondaryButtonText="Cancel"
          onRequestClose={() => setIssuedToken(null)}
          onRequestSubmit={() => setIssuedToken(null)}
          size="md">
          <InlineNotification
            lowContrast
            kind="warning"
            title="This is the only time this token will be shown."
            subtitle="Copy it now. Rotate or revoke it anytime from the grants list."
            hideCloseButton
            className="a2a-form-field"
          />
          <p>
            <strong>Bearer token:</strong>
          </p>
          <code
            style={{
              display: 'block',
              padding: '0.75rem',
              background: '#f4f4f4',
              borderRadius: '4px',
              wordBreak: 'break-all',
              margin: '0.5rem 0 1rem',
              fontSize: '0.875rem',
            }}>
            {issuedToken?.token}
          </code>
          <Button
            kind="tertiary"
            size="sm"
            renderIcon={Copy}
            onClick={() => copyToClipboard(issuedToken?.token)}>
            Copy token
          </Button>
          <p style={{ marginTop: '1rem', fontSize: '0.875rem' }}>
            <strong>Agent card URL to share:</strong>{' '}
            <code>https://freshfocusai.com/.well-known/agent.json</code>
          </p>
        </Modal>

        {/* ── Rotated-token modal ──────────────────────────────── */}
        <Modal
          open={!!rotatedToken}
          modalHeading={`Token rotated: "${rotatedToken?.label}"`}
          primaryButtonText="Done"
          secondaryButtonText="Cancel"
          onRequestClose={() => {
            setRotatedToken(null);
            loadGrants();
          }}
          onRequestSubmit={() => {
            setRotatedToken(null);
            loadGrants();
          }}
          size="md">
          <InlineNotification
            lowContrast
            kind="warning"
            title="This is the only time the new token will be shown."
            subtitle="The previous token is no longer valid."
            hideCloseButton
            className="a2a-form-field"
          />
          <code
            style={{
              display: 'block',
              padding: '0.75rem',
              background: '#f4f4f4',
              borderRadius: '4px',
              wordBreak: 'break-all',
              margin: '0.5rem 0 1rem',
              fontSize: '0.875rem',
            }}>
            {rotatedToken?.token}
          </code>
          <Button
            kind="tertiary"
            size="sm"
            renderIcon={Copy}
            onClick={() => copyToClipboard(rotatedToken?.token)}>
            Copy token
          </Button>
        </Modal>

        {/* ── New-remote modal ─────────────────────────────────── */}
        <Modal
          open={remoteModalOpen}
          modalHeading="Register a remote A2A agent"
          primaryButtonText={savingRemote ? 'Validating…' : 'Register'}
          primaryButtonDisabled={savingRemote}
          secondaryButtonText="Cancel"
          onRequestClose={() => setRemoteModalOpen(false)}
          onRequestSubmit={createRemote}
          size="md">
          <TextInput
            id="a2a-remote-name"
            labelText="Name"
            placeholder="e.g. Dylan-FFAI, AcmeBot"
            helperText="Your FFAI agent will use this name to refer to the remote."
            value={newRemoteName}
            onChange={e => setNewRemoteName(e.target.value)}
            className="a2a-form-field"
          />
          <TextInput
            id="a2a-remote-desc"
            labelText="Description (optional)"
            placeholder="What this agent does / when to call it"
            value={newRemoteDesc}
            onChange={e => setNewRemoteDesc(e.target.value)}
            className="a2a-form-field"
          />
          <TextInput
            id="a2a-remote-url"
            labelText="Agent card URL"
            placeholder="https://otheragent.com/.well-known/agent.json"
            helperText="The remote agent's public A2A discovery document."
            value={newRemoteCardUrl}
            onChange={e => setNewRemoteCardUrl(e.target.value)}
            className="a2a-form-field"
          />
          <TextInput
            id="a2a-remote-token"
            labelText="Bearer token"
            type="password"
            placeholder="a2a_xxxxxxxx..."
            helperText="The grant token the remote's owner issued to you."
            value={newRemoteToken}
            onChange={e => setNewRemoteToken(e.target.value)}
            autoComplete="off"
          />
          {newRemoteErr && (
            <p
              style={{
                color: '#da1e28',
                marginTop: '1rem',
                fontSize: '0.875rem',
              }}>
              {newRemoteErr}
            </p>
          )}
        </Modal>

        <div style={{ marginTop: '2rem' }}>
          <Button kind="secondary" onClick={() => nav('/')}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default A2AIntegrationPage;
